import { apiFetch, v1Path } from './api';
import { startOfDay } from './assigned-queue';

/* ------------------------------------------------------------------ */
/* Shared DTOs for the org-level Service Request views (§18/§24/§25/§27) */
/* ------------------------------------------------------------------ */

export type PersonRef = { user_id: string; name: string; email: string | null };

export type SlaDto = {
  requested_at: string;
  sla_date: string | null;
  target_deployment_date: string | null;
  days_remaining: number | null;
  overdue: boolean;
  at_risk: boolean;
  target_days_remaining: number | null;
  target_overdue: boolean;
};

export type Rag = 'green' | 'at_risk' | 'blocked';

export const SERVICE_REQUEST_STAGES = [
  'draft',
  'submitted',
  'under_review',
  'changes_required',
  'approved',
  'in_progress',
  'ready_for_deployment',
  'deployment_approval',
  'scheduled',
  'deployed',
  'validation',
  'completed',
  'rejected',
  'cancelled',
] as const;

export type Stage = (typeof SERVICE_REQUEST_STAGES)[number];

/** Stages hidden from the primary board by default (§23). */
export const CLOSED_OR_PARKED_STAGES: readonly Stage[] = ['changes_required', 'rejected', 'cancelled'];

export const STAGE_LABELS: Record<Stage, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  changes_required: 'Changes Required',
  approved: 'Approved',
  in_progress: 'In Progress',
  ready_for_deployment: 'Ready for Deployment',
  deployment_approval: 'Deployment Approval',
  scheduled: 'Scheduled',
  deployed: 'Deployed',
  validation: 'Validation',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export function isStage(value: string): value is Stage {
  return (SERVICE_REQUEST_STAGES as readonly string[]).includes(value);
}

export function stageLabel(stage: string | null | undefined): string {
  if (!stage) return '—';
  return isStage(stage) ? STAGE_LABELS[stage] : stage.replaceAll('_', ' ');
}

/** Board order for a stage; unknown stages sort last. */
export function stageIndex(stage: string): number {
  const index = (SERVICE_REQUEST_STAGES as readonly string[]).indexOf(stage);
  return index === -1 ? SERVICE_REQUEST_STAGES.length : index;
}

export function isClosedOrParkedStage(stage: string | null | undefined): boolean {
  return Boolean(stage) && (CLOSED_OR_PARKED_STAGES as readonly string[]).includes(stage as string);
}

/**
 * Tailwind tone for a stage chip. Text and dot are coloured together so the
 * meaning is never carried by colour alone.
 */
/**
 * Bordered pill tones, one semantic family per meaning: sky in progress, amber needs
 * attention, emerald approved, rose rejected, slate neutral. Dark mode uses a tint of
 * the same family rather than a solid fill.
 */
export function stageTone(stage: string | null | undefined): string {
  switch (stage) {
    case 'draft':
      return 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300';
    case 'submitted':
    case 'under_review':
      return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300';
    case 'changes_required':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300';
    case 'approved':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'in_progress':
    case 'ready_for_deployment':
      return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300';
    case 'deployment_approval':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300';
    case 'scheduled':
      return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300';
    case 'deployed':
    case 'validation':
      return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300';
    case 'completed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'rejected':
    case 'cancelled':
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300';
  }
}

/* ---------------------------- Priority ----------------------------- */

export const SERVICE_REQUEST_PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type ServiceRequestPriority = (typeof SERVICE_REQUEST_PRIORITIES)[number];

const PRIORITY_LABELS: Record<ServiceRequestPriority, string> = {
  urgent: 'Critical',
  high: 'High',
  medium: 'Normal',
  low: 'Low',
};

/** PRD §20 labels; `none` and unknown values render as an em dash. */
export function serviceRequestPriorityLabel(priority: string | null | undefined): string {
  if (!priority) return '—';
  return (PRIORITY_LABELS as Record<string, string>)[priority] ?? '—';
}

export function serviceRequestPriorityTone(priority: string | null | undefined): string {
  switch (priority) {
    case 'urgent':
      return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/15 dark:text-rose-300';
    case 'high':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300';
    case 'medium':
      return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/15 dark:text-sky-300';
    case 'low':
      return 'border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300';
    default:
      return 'text-muted-foreground';
  }
}

/* ------------------------- Delivery health ------------------------- */

export const RAG_LABELS: Record<Rag, string> = {
  green: 'Green',
  at_risk: 'At Risk',
  blocked: 'Blocked',
};

export function ragLabel(rag: string | null | undefined): string {
  return (RAG_LABELS as Record<string, string>)[rag ?? ''] ?? '—';
}

export function ragDotClass(rag: string | null | undefined): string {
  switch (rag) {
    case 'green':
      return 'bg-emerald-500';
    case 'at_risk':
      return 'bg-amber-500';
    case 'blocked':
      return 'bg-rose-500';
    default:
      return 'bg-slate-400';
  }
}

/* --------------------------- Approvals ----------------------------- */

export const APPROVAL_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'initial_pending', label: 'Initial · Pending' },
  { value: 'initial_approved', label: 'Initial · Approved' },
  { value: 'initial_changes_required', label: 'Initial · Changes required' },
  { value: 'initial_rejected', label: 'Initial · Rejected' },
  { value: 'deployment_not_requested', label: 'Deployment · Not requested' },
  { value: 'deployment_pending', label: 'Deployment · Pending' },
  { value: 'deployment_approved', label: 'Deployment · Approved' },
  { value: 'deployment_overridden', label: 'Deployment · Overridden' },
];

/* ------------------------------ List (§24) --------------------------- */

export type ServiceRequestListItem = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  state_id: string;
  state_name: string;
  stage: Stage | string;
  priority: string;
  customer: string | null;
  venue_site: string | null;
  request_type_id: string | null;
  request_type_name: string | null;
  owner: PersonRef | null;
  assignees: PersonRef[];
  responsible_department: string | null;
  target_go_live_date: string | null;
  required_completion_date: string | null;
  deployment_date: string | null;
  readiness_percent: number;
  submission_percent?: number;
  submission_completed?: number;
  submission_total?: number;
  submission_complete?: boolean;
  initial_approvals_done?: number;
  initial_approvals_required?: number;
  sla: SlaDto;
  initial_approval_status: string;
  deployment_approval_status: string;
  implementation_id: string | null;
  implementation_name: string | null;
  is_blocked: boolean;
  blockers: string[];
  rag: Rag;
  version: number;
  /** Rank inside its board column. */
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ServiceRequestListResponse = {
  items: ServiceRequestListItem[];
  page: { limit: number; offset: number; total: number };
};

export const SERVICE_REQUEST_ORDER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '-created_at', label: 'Newest' },
  { value: 'created_at', label: 'Oldest' },
  { value: '-updated_at', label: 'Recently updated' },
  { value: 'target_go_live_date', label: 'Target date (soonest)' },
  { value: '-target_go_live_date', label: 'Target date (latest)' },
  { value: 'sla_date', label: 'SLA date (soonest)' },
  { value: 'priority', label: 'Priority' },
  { value: '-readiness_percent', label: 'Readiness (high → low)' },
  { value: 'readiness_percent', label: 'Readiness (low → high)' },
  { value: 'key', label: 'SR number' },
];

export const SERVICE_REQUEST_DEFAULT_ORDER = '-created_at';
/** Matches `WORK_LIST_PAGE_SIZES` so the shared pagination control can be reused. */
export const SERVICE_REQUEST_PAGE_SIZE = 25;
export const SERVICE_REQUEST_PAGE_SIZES = [12, 25, 50] as const;

export type ServiceRequestListFilters = {
  q: string | null;
  customer: string | null;
  venue: string | null;
  department: string | null;
  request_type_id: string | null;
  owner_id: string | null;
  implementation_id: string | null;
  stage: string[];
  priority: string[];
  approval_status: string[];
  target_after: string | null;
  target_before: string | null;
  overdue: boolean;
  blocked: boolean;
  at_risk: boolean;
  readiness_min: number | null;
  readiness_max: number | null;
  order_by: string;
  limit: number;
  offset: number;
};

export const EMPTY_SERVICE_REQUEST_FILTERS: ServiceRequestListFilters = {
  q: null,
  customer: null,
  venue: null,
  department: null,
  request_type_id: null,
  owner_id: null,
  implementation_id: null,
  stage: [],
  priority: [],
  approval_status: [],
  target_after: null,
  target_before: null,
  overdue: false,
  blocked: false,
  at_risk: false,
  readiness_min: null,
  readiness_max: null,
  order_by: SERVICE_REQUEST_DEFAULT_ORDER,
  limit: SERVICE_REQUEST_PAGE_SIZE,
  offset: 0,
};

const TEXT_KEYS = ['q', 'customer', 'venue', 'department', 'request_type_id', 'owner_id', 'implementation_id', 'target_after', 'target_before'] as const;
const LIST_KEYS = ['stage', 'priority', 'approval_status'] as const;
const BOOL_KEYS = ['overdue', 'blocked', 'at_risk'] as const;

function text(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key);
  if (raw == null) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function csv(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return Array.from(new Set(raw.split(',').map((value) => value.trim()).filter(Boolean)));
}

function percent(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value)));
}

function isOrderBy(value: string): boolean {
  return SERVICE_REQUEST_ORDER_OPTIONS.some((option) => option.value === value);
}

/** URL search params → typed filter state. Unknown or malformed values fall back to defaults. */
export function parseServiceRequestListFilters(params: URLSearchParams): ServiceRequestListFilters {
  const filters: ServiceRequestListFilters = { ...EMPTY_SERVICE_REQUEST_FILTERS, stage: [], priority: [], approval_status: [] };
  for (const key of TEXT_KEYS) filters[key] = text(params, key);
  for (const key of LIST_KEYS) filters[key] = csv(params, key);
  for (const key of BOOL_KEYS) filters[key] = params.get(key) === 'true' || params.get(key) === '1';
  filters.readiness_min = percent(params, 'readiness_min');
  filters.readiness_max = percent(params, 'readiness_max');
  const orderBy = params.get('order_by');
  filters.order_by = orderBy && isOrderBy(orderBy) ? orderBy : SERVICE_REQUEST_DEFAULT_ORDER;
  const limit = Number(params.get('limit'));
  filters.limit = (SERVICE_REQUEST_PAGE_SIZES as readonly number[]).includes(limit) ? limit : SERVICE_REQUEST_PAGE_SIZE;
  const offset = Number(params.get('offset'));
  filters.offset = Number.isInteger(offset) && offset > 0 ? offset : 0;
  return filters;
}

/**
 * Typed filter state → URL search params. Only non-default values are written so
 * the address stays short; `keep` preserves params owned by other parts of the page
 * (e.g. `layout`, `peek`).
 */
export function serviceRequestListFiltersToSearchParams(
  filters: ServiceRequestListFilters,
  keep: URLSearchParams | null = null,
  keepKeys: readonly string[] = ['layout', 'peek', 'show_closed', 'show_parked', 'created', 'highlight'],
): URLSearchParams {
  const params = new URLSearchParams();
  if (keep) {
    for (const key of keepKeys) {
      const value = keep.get(key);
      if (value != null) params.set(key, value);
    }
  }
  for (const key of TEXT_KEYS) {
    const value = filters[key];
    if (value) params.set(key, value);
  }
  for (const key of LIST_KEYS) {
    const values = filters[key];
    if (values.length > 0) params.set(key, values.join(','));
  }
  for (const key of BOOL_KEYS) {
    if (filters[key]) params.set(key, 'true');
  }
  if (filters.readiness_min != null) params.set('readiness_min', String(filters.readiness_min));
  if (filters.readiness_max != null) params.set('readiness_max', String(filters.readiness_max));
  if (filters.order_by !== SERVICE_REQUEST_DEFAULT_ORDER) params.set('order_by', filters.order_by);
  if (filters.limit !== SERVICE_REQUEST_PAGE_SIZE) params.set('limit', String(filters.limit));
  if (filters.offset > 0) params.set('offset', String(filters.offset));
  return params;
}

/** Query string for `GET /service-requests`. */
export function serviceRequestListApiQuery(filters: ServiceRequestListFilters): string {
  const params = serviceRequestListFiltersToSearchParams(filters, null, []);
  params.set('order_by', filters.order_by);
  params.set('limit', String(filters.limit));
  params.set('offset', String(filters.offset));
  return params.toString();
}

/** Count of user-facing filters that are set (excludes ordering and paging). */
export function activeServiceRequestFilterCount(filters: ServiceRequestListFilters): number {
  let count = 0;
  for (const key of TEXT_KEYS) if (filters[key]) count += 1;
  for (const key of LIST_KEYS) if (filters[key].length > 0) count += 1;
  for (const key of BOOL_KEYS) if (filters[key]) count += 1;
  if (filters.readiness_min != null || filters.readiness_max != null) count += 1;
  return count;
}

export function toggleListValue(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

/* ------------------- Org-wide counts behind the list scopes ------------------- */

export type NeedsAttentionReason =
  | 'overdue'
  | 'blocked'
  | 'changes_required'
  | 'awaiting_approval'
  | 'missing_information';

export type NeedsAttentionRow = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  customer: string | null;
  owner: PersonRef | null;
  stage: Stage | string | null;
  target_go_live_date: string | null;
  reason: NeedsAttentionReason | string;
  message: string;
};

export type UpcomingDeployment = {
  id: string;
  key: string;
  title: string;
  project_id: string;
  customer: string | null;
  request_type_name: string | null;
  stage: Stage | string;
  date: string;
  target_go_live_date: string | null;
  days_until: number;
  readiness_percent: number;
  rag: Rag;
  reasons: string[];
  owner: PersonRef | null;
};

export type ServiceRequestDashboard = {
  as_of: string;
  total: number;
  open: number;
  awaiting_review: number;
  awaiting_approval: number;
  awaiting_deployment_approval: number;
  deployments_this_week: number;
  deployments_next_30_days: number;
  overdue: number;
  at_risk: number;
  blocked: number;
  by_stage: Array<{ stage: string; count: number }>;
  by_customer: Array<{ customer: string | null; count: number }>;
  by_owner: Array<{ owner: string | null; owner_id: string | null; count: number }>;
  by_type: Array<{ request_type: string | null; request_type_id: string | null; count: number }>;
  average_completion_days: number | null;
  completed_last_90_days: number;
  sla_performance: { met: number; breached: number; percent: number | null };
  /** The 90 days before the current period, for trends. */
  previous_period?: { average_completion_days: number | null; completed: number; sla_percent: number | null };
  /** Open requests per stage, in workflow order, with how long the longest has sat there. */
  flow?: Array<{ stage: string; count: number; oldest_days: number | null }>;
  /** Approval roles currently holding requests up. */
  waiting_on?: Array<{ role: string; phase: 'initial' | 'deployment'; count: number; oldest_days: number | null }>;
  /** Customers with open requests, their next go-live and worst health once delivery is agreed. */
  customers?: Array<{ customer: string; open: number; next_date: string | null; rag: Rag | null }>;
  rag_summary: { green: number; at_risk: number; blocked: number };
  upcoming_deployments: UpcomingDeployment[];
  needs_attention: NeedsAttentionRow[];
  needs_attention_total: number;
};

/* ----------------------------- Board cards --------------------------- */

/** What the stage itself is waiting on, when nothing is blocking the request. */
const STAGE_NEXT_ACTION: Record<string, string> = {
  draft: 'Complete request details',
  submitted: 'Awaiting review',
  under_review: 'Under review',
  changes_required: 'Address changes required',
  approved: 'Approved — ready for delivery',
  in_progress: 'Delivery in progress',
  ready_for_deployment: 'Request deployment approval',
  deployment_approval: 'Awaiting deployment approval',
  scheduled: 'Deployment scheduled',
  deployed: 'Confirm validation',
  validation: 'Confirm validation',
  completed: 'Completed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/**
 * One line telling the reader what has to happen next. A blocker always wins: it is the
 * concrete thing standing in the way, where the stage only says where the request sits.
 */
export function serviceRequestNextAction(item: Pick<ServiceRequestListItem, 'stage' | 'blockers' | 'state_name'>): string {
  const blocker = item.blockers.find((value) => value.trim().length > 0);
  if (blocker) return blocker;
  return STAGE_NEXT_ACTION[item.stage] ?? item.state_name;
}

const DELIVERY_READINESS_STAGES = new Set([
  'in_progress',
  'ready_for_deployment',
  'deployment_approval',
  'scheduled',
  'deployed',
  'validation',
  'completed',
]);

export type CardProgress =
  | { kind: 'percent'; label: string; percent: number }
  | { kind: 'steps'; label: string; done: number; total: number }
  | { kind: 'status'; label: string };

/** The one progress measure that matters for a card in its current stage. */
export function serviceRequestCardProgress(item: Pick<
  ServiceRequestListItem,
  'stage' | 'readiness_percent' | 'submission_percent' | 'submission_complete' | 'initial_approvals_done'
  | 'initial_approvals_required' | 'deployment_approval_status'
>): CardProgress {
  const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
  const stage = item.stage;
  if (stage === 'changes_required') return { kind: 'status', label: 'Changes requested' };
  if (stage === 'draft') {
    const percent = item.submission_percent ?? (item.submission_complete ? 100 : 0);
    return { kind: 'percent', label: 'Requirements', percent: clamp(percent) };
  }
  if (stage === 'submitted' || stage === 'under_review') {
    return { kind: 'steps', label: 'Pending approval', done: item.initial_approvals_done ?? 0, total: item.initial_approvals_required ?? 0 };
  }
  if (stage === 'approved') return { kind: 'status', label: 'Approved · handed to delivery' };
  if (stage === 'in_progress' || stage === 'ready_for_deployment') {
    return { kind: 'percent', label: 'Readiness', percent: clamp(item.readiness_percent) };
  }
  if (stage === 'deployment_approval') return { kind: 'status', label: 'Awaiting deployment approval' };
  if (stage === 'scheduled') return { kind: 'status', label: 'Deployment scheduled' };
  if (stage === 'deployed' || stage === 'validation') return { kind: 'status', label: 'Validating delivery' };
  if (stage === 'completed') return { kind: 'status', label: 'Completed' };
  return { kind: 'status', label: stageLabel(stage) };
}

export function showsDeliveryReadiness(stage: string | null | undefined): boolean {
  return Boolean(stage && DELIVERY_READINESS_STAGES.has(stage));
}

/* --------------------------- List scope chips ------------------------ */

export const OPEN_STAGES: readonly Stage[] = SERVICE_REQUEST_STAGES.filter(
  (stage) => stage !== 'completed' && stage !== 'rejected' && stage !== 'cancelled',
);

export type ServiceRequestScope = {
  key: string;
  label: string;
  /** Where the headline number comes from; the org-wide summary already counts open requests only. */
  count: (dashboard: ServiceRequestDashboard) => number;
  filters: Partial<ServiceRequestListFilters>;
  tone: 'default' | 'warn' | 'danger';
};

/**
 * The handful of questions people open the list to answer. Each chip shows the org-wide count
 * and narrows the list to the same set, so the number and the rows always agree.
 */
export const SERVICE_REQUEST_SCOPES: readonly ServiceRequestScope[] = [
  { key: 'open', label: 'Open', count: (d) => d.open, filters: { stage: [...OPEN_STAGES] }, tone: 'default' },
  {
    key: 'awaiting_review',
    label: 'Awaiting review',
    count: (d) => d.awaiting_review,
    filters: { stage: ['submitted'] },
    tone: 'default',
  },
  {
    key: 'deployment_approval',
    label: 'Deployment approval',
    count: (d) => d.awaiting_deployment_approval,
    filters: { stage: ['deployment_approval'] },
    tone: 'default',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    count: (d) => d.overdue,
    filters: { stage: [...OPEN_STAGES], overdue: true },
    tone: 'danger',
  },
  {
    key: 'blocked',
    label: 'Blocked',
    count: (d) => d.blocked,
    filters: { stage: [...OPEN_STAGES], blocked: true },
    tone: 'warn',
  },
];

function sameStages(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && [...left].sort().join() === [...right].sort().join();
}

/** True when the list is currently scoped to exactly this chip. */
export function matchesServiceRequestScope(filters: ServiceRequestListFilters, scope: ServiceRequestScope): boolean {
  return sameStages(filters.stage, scope.filters.stage ?? [])
    && filters.overdue === (scope.filters.overdue ?? false)
    && filters.blocked === (scope.filters.blocked ?? false)
    && filters.at_risk === (scope.filters.at_risk ?? false);
}

/** Scope patch that also resets the flags the other chips own, so chips stay mutually exclusive. */
export function serviceRequestScopePatch(
  scope: ServiceRequestScope,
  active: boolean,
): Partial<ServiceRequestListFilters> {
  const cleared = { stage: [], overdue: false, blocked: false, at_risk: false, offset: 0 };
  return active ? cleared : { ...cleared, ...scope.filters };
}

/* ----------------------------- Timeline (§25) ------------------------- */

export const TIMELINE_KINDS = [
  'target_go_live',
  'required_completion',
  'deployment',
  'site_survey',
  'installation',
  'content_deadline',
  'payment_onboarding',
  'logistics',
  'hardware_preparation',
  'task_due',
] as const;

export type TimelineKind = (typeof TIMELINE_KINDS)[number];

export const TIMELINE_KIND_LABELS: Record<TimelineKind, string> = {
  target_go_live: 'Target go-live',
  required_completion: 'Required completion',
  deployment: 'Deployment',
  site_survey: 'Site survey',
  installation: 'Installation',
  content_deadline: 'Content deadline',
  payment_onboarding: 'Payment onboarding',
  logistics: 'Logistics',
  hardware_preparation: 'Hardware preparation',
  task_due: 'Task due',
};

export function isTimelineKind(value: string): value is TimelineKind {
  return (TIMELINE_KINDS as readonly string[]).includes(value);
}

export function timelineKindLabel(kind: string): string {
  return isTimelineKind(kind) ? TIMELINE_KIND_LABELS[kind] : kind.replaceAll('_', ' ');
}

export function timelineKindTone(kind: string): string {
  switch (kind) {
    case 'target_go_live':
      return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300';
    case 'required_completion':
      return 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300';
    case 'deployment':
    case 'installation':
      return 'bg-violet-50 text-violet-800 dark:bg-violet-950 dark:text-violet-300';
    case 'site_survey':
    case 'hardware_preparation':
    case 'logistics':
      return 'bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-300';
    case 'content_deadline':
    case 'payment_onboarding':
      return 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export type TimelineItem = {
  work_item_id: string;
  work_item_key: string;
  project_id: string;
  title: string;
  customer: string | null;
  request_type_name: string | null;
  kind: TimelineKind | string;
  date: string;
  stage: Stage | string;
  rag: Rag;
  task_id: string | null;
  task_key: string | null;
  task_title: string | null;
  task_completed: boolean | null;
};

export type TimelineResponse = { from: string; to: string; items: TimelineItem[] };

export type TimelineRange = { from: string; to: string };

/** Local calendar date as YYYY-MM-DD. */
export function toDateOnly(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDays(value: Date, days: number): Date {
  const start = startOfDay(value);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + days);
}

function mondayOf(value: Date): Date {
  const start = startOfDay(value);
  const weekday = start.getDay();
  const back = weekday === 0 ? 6 : weekday - 1;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() - back);
}

/* --------------------------- Month calendar -------------------------- */

/** `YYYY-MM` for the month containing `value`. */
export function toMonthKey(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
}

/** First of the month for a `YYYY-MM` key, or null when malformed. */
export function parseMonthKey(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  const first = new Date(year, month - 1, 1);
  return Number.isNaN(first.getTime()) ? null : first;
}

export function addMonths(month: Date, delta: number): Date {
  return new Date(month.getFullYear(), month.getMonth() + delta, 1);
}

export type CalendarDay = {
  date: string;
  /** False for the leading/trailing days that pad the grid to whole weeks. */
  inMonth: boolean;
  items: TimelineItem[];
};

/** Six weeks keeps the calendar a stable height as months change. */
const MONTH_GRID_DAYS = 42;

/** The range the grid covers, which is what the timeline is fetched for. */
export function monthGridRange(month: Date): TimelineRange {
  const start = mondayOf(new Date(month.getFullYear(), month.getMonth(), 1));
  return { from: toDateOnly(start), to: toDateOnly(addDays(start, MONTH_GRID_DAYS - 1)) };
}

/** The grid's weeks, each item placed on its own day. Items outside the grid are dropped. */
export function buildMonthGrid(month: Date, items: readonly TimelineItem[]): CalendarDay[][] {
  const byDate = new Map<string, TimelineItem[]>();
  for (const item of items) {
    const list = byDate.get(item.date) ?? [];
    list.push(item);
    byDate.set(item.date, list);
  }
  const start = mondayOf(new Date(month.getFullYear(), month.getMonth(), 1));
  const weeks: CalendarDay[][] = [];
  for (let index = 0; index < MONTH_GRID_DAYS; index += 1) {
    const day = addDays(start, index);
    const date = toDateOnly(day);
    if (index % 7 === 0) weeks.push([]);
    weeks[weeks.length - 1]!.push({
      date,
      inMonth: day.getMonth() === month.getMonth(),
      items: (byDate.get(date) ?? []).slice().sort((a, b) => a.kind.localeCompare(b.kind)),
    });
  }
  return weeks;
}

/** Monday-first short weekday names in the viewer's locale. */
export function weekdayHeadings(locale?: string): string[] {
  const monday = new Date(2026, 0, 5);
  return Array.from({ length: 7 }, (_, index) =>
    addDays(monday, index).toLocaleDateString(locale, { weekday: 'short' }));
}

/** Month and kind filters from the URL; defaults to the month containing `today`. */
export function parseTimelineCalendarQuery(
  params: URLSearchParams,
  today: Date,
): { month: Date; kinds: TimelineKind[] } {
  return {
    month: parseMonthKey(params.get('month')) ?? new Date(today.getFullYear(), today.getMonth(), 1),
    kinds: csv(params, 'kind').filter(isTimelineKind),
  };
}

export function timelineApiQuery(range: TimelineRange, kinds: readonly string[]): string {
  const params = new URLSearchParams();
  params.set('from', range.from);
  params.set('to', range.to);
  if (kinds.length > 0) params.set('kind', kinds.join(','));
  return params.toString();
}

/** Route for a timeline row: the task when one is attached, else the Service Request. */
export function timelineItemPath(item: Pick<TimelineItem, 'work_item_id' | 'project_id' | 'task_id'>): string {
  if (item.task_id) return `/projects/${item.project_id}/work-items/${item.task_id}`;
  return `/projects/${item.project_id}/service-requests/${item.work_item_id}`;
}

/* -------------------------- Implementations (§18) -------------------- */

export type ImplementationSlim = {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  customer: string | null;
  target_date: string | null;
  created_at: string;
  updated_at: string;
  request_count: number;
  readiness_percent: number;
  blocked_count: number;
};

export type ImplementationRequest = {
  work_item: {
    id: string;
    key: string;
    title: string;
    project_id: string;
    state_id: string;
    state_name: string;
    stage: Stage | string;
    priority: string;
    due_date: string | null;
    version: number;
  };
  request_type_id: string | null;
  request_type_name: string | null;
  customer: string | null;
  owner: PersonRef | null;
  target_go_live_date: string | null;
  deployment_date: string | null;
  readiness_percent: number;
  sla: SlaDto;
  initial_approval_status: string;
  deployment_approval_status: string;
  is_blocked: boolean;
  rag: Rag;
  blockers: string[];
};

export type ImplementationBlocker = {
  work_item_id: string;
  work_item_key: string;
  code: string;
  message: string;
  hard: boolean;
};

export type ImplementationDetail = {
  implementation: ImplementationSlim;
  requests: ImplementationRequest[];
  readiness_percent: number;
  request_count: number;
  open_count: number;
  blocked_count: number;
  blockers: ImplementationBlocker[];
  counts_by_stage: Partial<Record<Stage, number>>;
};

export function implementationPath(id: string): string {
  return `/service-requests/implementations/${id}`;
}

export type ServiceRequestBackTarget = { path: string; label: string };

/** Router `state` for links into a request, so its back link returns to the page it was opened from. */
export function serviceRequestFrom(path: string, label: string): { from: ServiceRequestBackTarget } {
  return { from: { path, label } };
}

export function serviceRequestBackTarget(state: unknown): ServiceRequestBackTarget {
  const from = (state as { from?: Partial<ServiceRequestBackTarget> } | null)?.from;
  if (from && typeof from.path === 'string' && from.path.startsWith('/') && typeof from.label === 'string' && from.label.trim()) {
    return { path: from.path, label: from.label };
  }
  return { path: '/service-requests', label: 'Service Requests' };
}

/* ------------------------------ Nav --------------------------------- */

export type ServiceRequestView = 'board' | 'list' | 'calendar' | 'implementations';

export const SERVICE_REQUEST_VIEWS: ReadonlyArray<{ id: ServiceRequestView; label: string; to: string }> = [
  { id: 'board', label: 'Board', to: '/service-requests?layout=kanban' },
  { id: 'list', label: 'List', to: '/service-requests?layout=list' },
  { id: 'calendar', label: 'Calendar', to: '/service-requests/calendar' },
  { id: 'implementations', label: 'Implementations', to: '/service-requests/implementations' },
];

/* ------------------------------ Surfaces ----------------------------- */

/** Same surface as the shared Card, for panels that are not the Card component. */
export const SR_PANEL = 'overflow-hidden rounded-md border bg-card text-card-foreground';

/** Which SR view a location resolves to, or null outside the Service Request area. */
export function serviceRequestViewForLocation(pathname: string, search: string): ServiceRequestView | null {
  if (pathname === '/service-requests/calendar') return 'calendar';
  if (pathname === '/service-requests/implementations' || pathname.startsWith('/service-requests/implementations/')) {
    return 'implementations';
  }
  if (pathname === '/service-requests') {
    const layout = new URLSearchParams(search).get('layout');
    return layout === 'list' ? 'list' : 'board';
  }
  return null;
}

export function isServiceRequestArea(pathname: string): boolean {
  return pathname === '/service-requests' || pathname.startsWith('/service-requests/');
}

/* ------------------------------ Fetchers ------------------------------ */

export function fetchServiceRequestList(filters: ServiceRequestListFilters): Promise<ServiceRequestListResponse> {
  return apiFetch<ServiceRequestListResponse>(`${v1Path('/service-requests')}?${serviceRequestListApiQuery(filters)}`);
}

/** The API's ceiling on one page of requests. */
const BOARD_PAGE_LIMIT = 200;
const BOARD_MAX_PAGES = 3;

/**
 * Every request matching the filters, since a board shows all of its columns at once.
 * `capped` says more exist than were fetched, so the board can admit it is partial.
 */
export async function fetchServiceRequestBoard(
  filters: ServiceRequestListFilters,
): Promise<{ items: ServiceRequestListItem[]; total: number; capped: boolean }> {
  const items: ServiceRequestListItem[] = [];
  let total = 0;
  for (let page = 0; page < BOARD_MAX_PAGES; page += 1) {
    const res = await fetchServiceRequestList({
      ...filters,
      limit: BOARD_PAGE_LIMIT,
      offset: page * BOARD_PAGE_LIMIT,
    });
    total = res.page.total;
    items.push(...res.items);
    if (items.length >= total || res.items.length === 0) break;
  }
  return { items, total, capped: items.length < total };
}

export function fetchServiceRequestDashboard(): Promise<ServiceRequestDashboard> {
  return apiFetch<ServiceRequestDashboard>(v1Path('/service-requests/dashboard'));
}

export function fetchServiceRequestTimeline(range: TimelineRange, kinds: readonly string[]): Promise<TimelineResponse> {
  return apiFetch<TimelineResponse>(`${v1Path('/service-requests/timeline')}?${timelineApiQuery(range, kinds)}`);
}

export function fetchImplementations(): Promise<ImplementationSlim[]> {
  return apiFetch<{ items: ImplementationSlim[] }>(v1Path('/service-request-implementations')).then((res) => res.items);
}

export function fetchImplementation(id: string): Promise<ImplementationDetail> {
  return apiFetch<ImplementationDetail>(v1Path(`/service-request-implementations/${encodeURIComponent(id)}`));
}

export type ImplementationInput = {
  name: string;
  description?: string | null;
  customer?: string | null;
  target_date?: string | null;
};

export function createImplementation(input: ImplementationInput): Promise<ImplementationSlim> {
  return apiFetch<ImplementationSlim>(v1Path('/service-request-implementations'), {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateImplementation(id: string, patch: Partial<ImplementationInput>): Promise<ImplementationDetail> {
  return apiFetch<ImplementationDetail>(v1Path(`/service-request-implementations/${encodeURIComponent(id)}`), {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function personName(person: PersonRef | null | undefined): string {
  if (!person) return '—';
  if (person.name && person.name.trim().length > 0) return person.name;
  if (person.email) return person.email.split('@')[0] || person.email;
  return person.user_id;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${Math.round(value)}%`;
}
